import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import {
  useClaimBingoRatingPrizeMutation,
  useGetBingoLeaderboardQuery,
} from '@/api/queries/bingo';
import { useGetMyInfoMutation } from '@/api/queries/user';
import { opacityAnimation } from '@/common/config/motion-animations';
import { type BingoMyRatingPrize } from '@/common/types/bingo';
import { getDecorationClass } from '@/components/Leagues/PositionItem/decorations/registry';
import { RewardItem } from '@/components/Leagues/RewardItem/RewardItem';
import { WinnerPosition } from '@/components/Leagues/WinnerPosition/WinnerPosition';
import { H1, StringSmall } from '@/components/Typography/Typography';
import { MainButton } from '@/components/ui/Button/Button';
import { GlowEffect } from '@/components/ui/GlowEffect/GlowEffect';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

import starsPng from '/img/modals/stars-1.png';
import starsWebp from '/img/modals/stars-1.webp';

interface BingoRatingClaimModalProps {
  isOpen: boolean;
  myRatingPrize: BingoMyRatingPrize | null;
  onClose: () => void;
}

const BingoRatingClaimModal: FC<BingoRatingClaimModalProps> = ({
  isOpen,
  myRatingPrize,
  onClose,
}) => {
  const { t } = useTranslation();
  const myAvatarUrl = useAppSelector((state) => state.user.user?.photoUrl);
  const [claim, { isLoading }] = useClaimBingoRatingPrizeMutation();
  const [refreshUser] = useGetMyInfoMutation();
  const { refetch } = useGetBingoLeaderboardQuery();

  if (!myRatingPrize) return null;

  const { place, prize } = myRatingPrize;
  const decorationClass = getDecorationClass(prize.decoration_id);

  // BingoPrize: wallet/materials/lottery — Record<string, number>; итерируем
  // динамически, без хардкода набора валют/материалов/билетов.
  const rewardEntries: Array<[string, number]> = [
    ...Object.entries(prize.wallet ?? {}),
    ...Object.entries(prize.materials ?? {}),
    ...Object.entries(prize.lottery ?? {}),
  ];

  const handleClaim = async (): Promise<void> => {
    try {
      const result = await claim().unwrap();

      await refreshUser().unwrap();
      refetch();
      onClose();

      if (result.rewards.fallback) {
        showToast(
          t('Skin already owned — received {{amount}} {{currency}} instead', {
            amount: result.rewards.fallback.amount,
            currency: result.rewards.fallback.currency.toUpperCase(),
          }),
        );
      }
    } catch (e) {
      const error = e as { data?: { message?: string } };
      showToast(error.data?.message || t('Failed to claim, please retry'));
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div className={css.claimModal} {...opacityAnimation}>
          <H1 weight="bold" className="mb-4">
            {t('Congrats!')}
          </H1>
          {myAvatarUrl && (
            <div className="flex items-center flex-col justify-end h-[4.625rem]">
              <WinnerPosition
                size="large"
                className="mb-1"
                position={place}
                avatar={myAvatarUrl}
              />
            </div>
          )}
          <StringSmall weight="regular" className="!text-white/60 mb-6">
            {t('You took {{position}}st place in the event', {
              position: place,
            })}
          </StringSmall>
          <div className={css.rewards}>
            {decorationClass && (
              <div
                className={`${css.decoration} ${decorationClass}`}
                aria-label={prize.decoration_id}
              />
            )}
            {rewardEntries.map(([item, quantity]) => (
              <RewardItem
                key={`reward-${item}`}
                item={item}
                quantity={quantity}
              />
            ))}
          </div>
          <div className="w-[12.5rem] h-[12.5rem] relative mb-10">
            <GlowEffect className="w-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -z-[1]" />
            <div
              style={{
                backgroundImage: `image-set("${starsPng}" type("image/png"), "${starsWebp}" type("image/webp"))`,
              }}
              className={css.stars}
            ></div>
            <img
              src="/img/bingo/bingo-icon.png"
              alt="bingo"
              className="w-full h-full object-contain"
            />
          </div>
          <MainButton
            disabled={isLoading}
            isLoading={isLoading}
            onClick={handleClaim}
            text={t('Claim')}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { BingoRatingClaimModal };
